import React, { useMemo, useRef, useState } from "react";
import { consoleApi } from "../../../services/consoleApiService";
import { useConsoleFetch } from "../../../hooks/useConsoleFetch";

const CATEGORY_OPTIONS = [
  { value: "motion-graphic", label: "Motion Graphic" },
  { value: "reel", label: "Reel" },
  { value: "app-recording", label: "App Recording" },
  { value: "b-roll", label: "B-Roll" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABELS = CATEGORY_OPTIONS.reduce((acc, c) => {
  acc[c.value] = c.label;
  return acc;
}, {});

function formatBytes(n) {
  if (!n) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="ch-lib__lightbox" onClick={onClose}>
      <div className="ch-lib__lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <video
          src={item.url}
          controls
          autoPlay
          loop
          playsInline
          style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8, background: "#000" }}
        />
        <div className="ch-lib__lightbox-meta">
          <div className="ch-lib__lightbox-topic">{item.label}</div>
          <div className="ch-lib__lightbox-tags">
            <span className="content-hub__tag">{CATEGORY_LABELS[item.category] || item.category}</span>
            {item.size_bytes && (
              <span className="content-hub__tag content-hub__tag--soft">
                {formatBytes(item.size_bytes)}
              </span>
            )}
            {item.content_type && (
              <span className="content-hub__tag content-hub__tag--soft">{item.content_type}</span>
            )}
          </div>
          <div className="ch-lib__lightbox-actions">
            <a href={item.url} target="_blank" rel="noreferrer" className="content-hub__btn">
              Open original
            </a>
            <button type="button" className="content-hub__btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MediaView() {
  const [filterCategory, setFilterCategory] = useState("");
  const [active, setActive] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("motion-graphic");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const fileInputRef = useRef(null);

  const { data: items, loading, refetch } = useConsoleFetch(
    () => consoleApi.content.listMedia().then((r) => (Array.isArray(r) ? r : [])),
    [],
    { refetchOnWindowFocus: false }
  );

  const list = useMemo(() => {
    const all = Array.isArray(items) ? items : [];
    return filterCategory ? all.filter((i) => i.category === filterCategory) : all;
  }, [items, filterCategory]);

  const totalCount = (Array.isArray(items) ? items : []).length;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}…`);
    setError(null);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", uploadCategory);
        fd.append("label", file.name);
        await consoleApi.content.uploadMedia(fd);
      }
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const handleBackfill = async () => {
    setBusy("Indexing existing brief videos…");
    setError(null);
    try {
      const r = await consoleApi.content.backfillMedia();
      await refetch();
      setBusy(`Indexed ${r?.reels_indexed ?? 0} reels and ${r?.clips_indexed ?? 0} animations`);
      setTimeout(() => setBusy(null), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Backfill failed");
      setBusy(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    setBusy("Deleting…");
    setError(null);
    try {
      await consoleApi.content.deleteMedia(id);
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const startRename = (item) => {
    setEditingId(item.id);
    setEditLabel(item.label || "");
    setEditCategory(item.category || "other");
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditLabel("");
    setEditCategory("");
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) {
      cancelRename();
      return;
    }
    setBusy("Saving…");
    setError(null);
    try {
      await consoleApi.content.updateMedia(id, { label, category: editCategory });
      cancelRename();
      await refetch();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      (f.type || "").startsWith("video/")
    );
    if (files.length > 0) handleFiles(files);
  };

  return (
    <div className="ch-shots">
      {/* Upload zone */}
      <div
        className={`ch-shots__drop ${dragOver ? "is-dragover" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div className="ch-shots__drop-text">
          <div className="ch-shots__drop-title">Drop videos here</div>
          <div className="ch-shots__drop-sub">MP4, MOV, WebM — reels, motion graphics, app recordings. Persists across deploys.</div>
        </div>
        <div className="ch-shots__drop-actions">
          <label className="ch-shots__drop-cat">
            <span>Category</span>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="content-hub__btn content-hub__btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!!busy}
          >
            Choose files
          </button>
          <button
            type="button"
            className="content-hub__btn"
            onClick={handleBackfill}
            disabled={!!busy}
            title="Index reels and animations from existing briefs into this bank"
          >
            Backfill from briefs
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              handleFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {busy && <div className="content-hub__busy">{busy}</div>}
      {error && <div className="content-hub__error">{error}</div>}

      {/* Toolbar */}
      <div className="ch__toolbar">
        <div className="ch__toolbar-left">
          <div className="ch__toolbar-title">
            <span className="ch__toolbar-eyebrow">Media</span>
            <span className="ch__toolbar-count">
              {list.length} {list.length === 1 ? "video" : "videos"}
              {totalCount !== list.length && (
                <span className="ch-shots__source-split">
                  {" · "}{totalCount} total
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="ch__filters">
          <select
            className="ch__select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {filterCategory && (
            <button
              type="button"
              className="ch__btn ch__btn--link"
              onClick={() => setFilterCategory("")}
            >
              Clear filter
            </button>
          )}
        </div>
      </div>

      {loading && <p className="content-hub__hint">Loading media…</p>}
      {!loading && list.length === 0 && (
        <div className="content-hub__empty">
          <p>
            {filterCategory
              ? `No videos in "${CATEGORY_LABELS[filterCategory] || filterCategory}". Try clearing the filter.`
              : "No videos yet. Drop some above to start building your media library."}
          </p>
        </div>
      )}

      <div className="ch-lib__grid">
        {list.map((item) => (
          <div key={item.id} className="ch-lib__card ch-shots__card ch-shots__card--uploaded">
            <button
              type="button"
              className="ch-shots__card-thumb-btn"
              onClick={() => setActive(item)}
              title={item.label}
            >
              <div className="ch-lib__thumb">
                <video
                  src={item.url}
                  preload="metadata"
                  muted
                  playsInline
                  loop
                  onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                  onMouseLeave={(e) => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                  }}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <span
                  className="ch-shots__source-badge ch-shots__source-badge--uploaded"
                  title={
                    item.source_kind === "reel"
                      ? "Auto-saved reel from a brief"
                      : item.source_kind === "animation"
                      ? "Auto-saved slide animation from a brief"
                      : "Uploaded via UI"
                  }
                >
                  {item.source_kind === "reel"
                    ? "Reel"
                    : item.source_kind === "animation"
                    ? "Clip"
                    : "Video"}
                </span>
              </div>
            </button>
            <div className="ch-lib__card-meta">
              {editingId === item.id ? (
                <div className="ch-shots__rename">
                  <input
                    type="text"
                    value={editLabel}
                    autoFocus
                    maxLength={200}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename(item.id);
                      if (e.key === "Escape") cancelRename();
                    }}
                    placeholder="Descriptive title"
                  />
                  <select
                    className="ch__select"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <div className="ch-shots__rename-actions">
                    <button type="button" className="ch-shots__copy-btn" onClick={() => saveRename(item.id)}>Save</button>
                    <button type="button" className="ch-shots__delete-btn" onClick={cancelRename}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="ch-lib__card-topic">{item.label}</div>
              )}
              <div className="ch-lib__card-tags">
                <span className="content-hub__tag">{CATEGORY_LABELS[item.category] || item.category}</span>
                {item.size_bytes && (
                  <span className="content-hub__tag content-hub__tag--soft">
                    {formatBytes(item.size_bytes)}
                  </span>
                )}
              </div>
              {editingId !== item.id && (
                <div className="ch-shots__card-actions">
                  <button
                    type="button"
                    className="ch-shots__copy-btn"
                    onClick={() => {
                      navigator.clipboard?.writeText(item.url);
                    }}
                    title="Copy video URL"
                  >
                    Copy URL
                  </button>
                  <button
                    type="button"
                    className="ch-shots__copy-btn"
                    onClick={() => startRename(item)}
                    title="Rename / recategorize"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="ch-shots__delete-btn"
                    onClick={() => handleDelete(item.id)}
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Lightbox item={active} onClose={() => setActive(null)} />
    </div>
  );
}
